import React, { useState, useEffect } from 'react';
import { Plus, Wrench } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/AuthContext';
import { maintenance as api, units as unitsApi, residents as residentsApi, type MaintenanceRequest, type Unit, type Resident, type ApiError } from '../../services/api';
import DataGrid from '../../components/DataGrid';
import SearchSelect from '../../components/SearchSelect';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

const INIT = { title: '', description: '', category: '', unitId: '', scheduledAt: '' };

const STATUS_OPTIONS: MaintenanceRequest['status'][] = ['pending', 'scheduled', 'in-progress', 'completed', 'cancelled'];

// Normalise backend status to lowercase-hyphen form for safe lookup
const normaliseStatus = (s: string | undefined): string =>
  (s ?? '').toLowerCase().replace(/_/g, '-');

const statusColor = (raw: string | undefined) => {
  const s = normaliseStatus(raw);
  return s === 'completed'   ? { bg: 'rgba(16,185,129,0.1)',  text: '#10B981' }
    :    s === 'in-progress' ? { bg: 'rgba(26,108,246,0.1)',  text: '#1A6CF6' }
    :    s === 'scheduled'   ? { bg: 'rgba(109,40,217,0.1)',  text: '#6D28D9' }
    :    s === 'cancelled'   ? { bg: 'rgba(148,163,184,0.1)', text: '#64748B' }
    :                          { bg: 'rgba(245,158,11,0.1)',  text: '#D97706' };
};

const CATEGORIES = ['Plumbing', 'Electrical', 'Carpentry', 'Painting', 'Lift', 'HVAC', 'Landscaping', 'Other'];

const MaintenancePage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const [items, setItems] = useState<MaintenanceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(INIT);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [unitOptions, setUnitOptions] = useState<Unit[]>([]);
  const [residentOptions, setResidentOptions] = useState<Resident[]>([]);

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  useEffect(() => {
    if (!societyId) return;
    unitsApi.list(societyId, { size: 500 }).then(page => setUnitOptions(page.content)).catch(() => {});
    residentsApi.list(societyId, { size: 500 }).then(page => setResidentOptions(page.content)).catch(() => {});
  }, [societyId]);

  const residentForUnit = (unitId: string) => residentOptions.find(r => r.unitId === unitId);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError('');
    try {
      const created = await api.create(societyId, {
        title: form.title, description: form.description || undefined,
        category: form.category || undefined, unitId: form.unitId || undefined,
        scheduledAt: form.scheduledAt || undefined,
      });
      setItems(p => [created, ...p]);
      setShowForm(false); setForm(INIT);
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to create request');
    } finally { setSaving(false); }
  };

  const updateStatus = async (id: string, status: MaintenanceRequest['status']) => {
    if (!societyId) return;
    setUpdatingId(id);
    try {
      const updated = await api.updateStatus(societyId, id, status);
      setItems(p => p.map(m => m.id === id ? updated : m));
    } catch { /* ignore */ } finally { setUpdatingId(null); }
  };

  const fmt = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Maintenance
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{items.filter(m => normaliseStatus(m.status) === 'pending').length} pending · {items.length} total</p>
        </div>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> New Request
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            {
              key: 'title', header: 'Title',
              cellStyle: { fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              render: (m: MaintenanceRequest) => m.title,
            },
            {
              key: 'unit', header: 'Unit / Resident',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              render: (m: MaintenanceRequest) => (m.unitNumber || m.residentName) ? (
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--nx-text-primary)' }}>
                    {[m.blockName, m.unitNumber].filter(Boolean).join(' · ') || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
                    {m.residentName || '—'}{m.residentPhone ? ` · ${m.residentPhone}` : ''}
                  </div>
                </div>
              ) : '—',
            },
            {
              key: 'category', header: 'Category',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              render: (m: MaintenanceRequest) => m.category || '—',
            },
            {
              key: 'status', header: 'Status',
              render: (m: MaintenanceRequest) => {
                const norm = normaliseStatus(m.status);
                const sc = statusColor(norm);
                const label = norm === 'in-progress' ? 'In Progress'
                  : norm ? norm.charAt(0).toUpperCase() + norm.slice(1) : '—';
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                    {label}
                  </span>
                );
              },
            },
            {
              key: 'scheduled', header: 'Scheduled',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (m: MaintenanceRequest) => fmt(m.scheduledAt),
            },
            {
              key: 'completed', header: 'Completed',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (m: MaintenanceRequest) => fmt(m.completedAt),
            },
            {
              key: 'update', header: 'Update Status',
              render: (m: MaintenanceRequest) => {
                const norm = normaliseStatus(m.status);
                const selectValue = STATUS_OPTIONS.includes(norm as MaintenanceRequest['status'])
                  ? norm : STATUS_OPTIONS[0];
                return (
                  <select
                    value={selectValue}
                    disabled={updatingId === m.id}
                    onChange={ev => updateStatus(m.id, ev.target.value as MaintenanceRequest['status'])}
                    style={{ ...inp, padding: '4px 8px', fontSize: 11, width: 'auto' }}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>
                        {s === 'in-progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                );
              },
            },
          ]}
          rows={items}
          rowKey={m => m.id}
          loading={loading}
          emptyIcon={<Wrench size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No maintenance requests yet."
        />
      </div>

      {showForm && (
        <Modal
          title="New Maintenance Request" submitLabel="Submit Request" error={formError} isLoading={saving}
          onClose={() => { setShowForm(false); setForm(INIT); setFormError(''); }}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Title *</label>
            <input name="title" value={form.title} onChange={change} required placeholder="Water pipe leakage in Block A" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Category</label>
              <select name="category" value={form.category} onChange={change} style={inp}>
                <option value="">Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c.toLowerCase()}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Unit</label>
              <SearchSelect
                value={form.unitId}
                onChange={v => setForm(p => ({ ...p, unitId: v }))}
                options={unitOptions.map(u => ({ value: u.id, label: u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber }))}
                placeholder={unitOptions.length === 0 ? 'No units available' : 'Select unit…'}
                emptyText="No units found"
                disabled={unitOptions.length === 0}
              />
            </div>
          </div>
          {form.unitId && (
            <div style={{ padding: '8px 12px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 12, color: 'var(--nx-text-secondary)' }}>
              {(() => {
                const resident = residentForUnit(form.unitId);
                return resident
                  ? <><strong style={{ color: 'var(--nx-text-primary)' }}>{resident.fullName}</strong>{resident.phone ? ` · ${resident.phone}` : ''}</>
                  : 'No resident on record for this unit.';
              })()}
            </div>
          )}
          <div>
            <label style={lbl}>Description</label>
            <textarea
              name="description" value={form.description}
              onChange={change} placeholder="Detailed description of the issue…"
              style={{ ...inp, minHeight: 80, resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={lbl}>Scheduled At</label>
            <input type="datetime-local" name="scheduledAt" value={form.scheduledAt} onChange={change} style={inp} />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default MaintenancePage;
